import React, { useState, useEffect, useRef } from 'react';
import { FaLock, FaCheck, FaEye, FaEyeSlash } from 'react-icons/fa';
import Footer from '../components/Footer';
import useEditorStore from '../store/useEditorStore';

interface UserInfo {
  name: string;
  email: string;
  createdAt: string;
}

const Settings: React.FC = () => {
  const { isDarkMode } = useEditorStore();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loadingUser, setLoadingUser] = useState(true);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [mounted, setMounted] = useState(false);

  const formRef = useRef<HTMLFormElement>(null);

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001';

  useEffect(() => {
    requestAnimationFrame(() => setMounted(true));
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setUser(data);
        }
      } catch {
        // silently fail
      } finally {
        setLoadingUser(false);
      }
    };
    fetchUser();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.message || 'Failed to update password');
        return;
      }

      setSuccess('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const passwordStrength = (() => {
    if (!newPassword) return 0;
    let s = 0;
    if (newPassword.length >= 6) s++;
    if (newPassword.length >= 10) s++;
    if (/[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword)) s++;
    if (/[0-9]/.test(newPassword)) s++;
    if (/[^A-Za-z0-9]/.test(newPassword)) s++;
    return s;
  })();

  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong', 'Excellent'][passwordStrength];
  const strengthColor = ['', '#ef4444', '#f59e0b', '#3b82f6', '#10b981', '#059669'][passwordStrength];

  const inputClass = 'w-full rounded-xl border-2 border-gray-200 bg-gray-50/80 px-4 py-3.5 pr-12 text-sm font-medium text-gray-900 transition-all duration-200 placeholder:font-normal placeholder:text-gray-400 focus:border-indigo-500 focus:bg-white focus:shadow-[0_0_0_3px_rgba(99,102,241,0.12)] focus:outline-none dark:border-gray-600 dark:bg-gray-700/50 dark:text-white dark:placeholder:text-gray-500 dark:focus:border-indigo-400';

  return (
    <div className="relative flex min-h-[calc(100vh-48px)] flex-col overflow-hidden bg-[#fafafa] dark:bg-[#0a0a0a]" style={{ fontFamily: "'Geist', sans-serif" }}>
      {/* Animated mesh gradient background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute -top-[40%] -right-[20%] h-[80%] w-[70%] rounded-full opacity-[0.07] blur-[120px] dark:opacity-[0.12]"
          style={{
            background: isDarkMode
              ? 'radial-gradient(circle, #6366f1, #8b5cf6)'
              : 'radial-gradient(circle, #6366f1, #a855f7)',
            animation: 'settingsPulse 8s ease-in-out infinite alternate',
          }}
        />
        <div
          className="absolute -bottom-[30%] -left-[20%] h-[60%] w-[60%] rounded-full opacity-[0.05] blur-[100px] dark:opacity-[0.1]"
          style={{
            background: isDarkMode
              ? 'radial-gradient(circle, #8b5cf6, #6366f1)'
              : 'radial-gradient(circle, #0d9488, #6366f1)',
            animation: 'settingsPulse 10s ease-in-out 2s infinite alternate',
          }}
        />
      </div>

      <div className="relative mx-auto w-full max-w-[580px] flex-1 px-5 py-12 sm:px-0">
        {/* Profile Hero */}
        <div
          className="mb-10 text-center"
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.7s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          {loadingUser ? (
            <div className="flex flex-col items-center gap-4">
              <div className="h-[88px] w-[88px] animate-pulse rounded-full bg-gray-200 dark:bg-gray-800" />
              <div className="h-5 w-32 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-800" />
              <div className="h-4 w-48 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800/50" />
            </div>
          ) : user ? (
            <>
              {/* Avatar */}
              <div className="relative mx-auto mb-5 h-[88px] w-[88px]">
                <div
                  className="absolute -inset-[3px] rounded-full opacity-80 blur-sm"
                  style={{
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7)',
                  }}
                />
                <div
                  className="relative flex h-full w-full items-center justify-center rounded-full text-2xl font-bold tracking-wide text-white"
                  style={{
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  }}
                >
                  {getInitials(user.name)}
                </div>
              </div>

              <h1 className="text-[28px] font-bold tracking-tight text-gray-900 dark:text-white">
                {user.name}
              </h1>
              <p className="mt-1 text-[15px] text-gray-400 dark:text-gray-500">
                {user.email}
              </p>
              <p className="mt-2 text-[12px] tracking-wide text-gray-300 dark:text-gray-600">
                Member since {formatDate(user.createdAt)}
              </p>
            </>
          ) : (
            <p className="text-sm text-gray-400">Unable to load profile</p>
          )}
        </div>

        {/* Security Card */}
        <div
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.15s',
          }}
        >
          <div
            className="overflow-hidden rounded-2xl border border-gray-200/50 bg-white/70 shadow-[0_0_0_1px_rgba(0,0,0,0.02),0_2px_8px_rgba(0,0,0,0.04),0_12px_40px_rgba(0,0,0,0.04)] backdrop-blur-xl dark:border-gray-800/50 dark:bg-gray-900/50 dark:shadow-[0_0_0_1px_rgba(255,255,255,0.03),0_2px_8px_rgba(0,0,0,0.2),0_12px_40px_rgba(0,0,0,0.2)] lg:rounded-[20px]"
          >
            {/* Card Header */}
            <div className="flex items-center gap-3 px-7 pb-1 pt-7">
              <div
                className="flex h-8 w-8 items-center justify-center rounded-[10px] text-white"
                style={{
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                }}
              >
                <FaLock size={12} />
              </div>
              <div>
                <h2 className="text-[15px] font-semibold text-gray-900 dark:text-white">
                  Security
                </h2>
                <p className="text-[12px] text-gray-400 dark:text-gray-500">
                  Update your password
                </p>
              </div>
            </div>

            {/* Form */}
            <form ref={formRef} onSubmit={handleSubmit} className="px-7 pb-7 pt-5">
              <div className="space-y-4">
                {/* Current Password */}
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                      className={inputClass}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-gray-400 transition-colors hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                      aria-label={showPassword ? 'Hide passwords' : 'Show passwords'}
                    >
                      {showPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                    </button>
                  </div>
                </div>

                {/* Divider */}
                <div className="flex items-center gap-3 py-1">
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-200 to-transparent dark:via-gray-700/50" />
                  <span className="text-[10px] font-medium uppercase tracking-widest text-gray-300 dark:text-gray-600">
                    new
                  </span>
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-200 to-transparent dark:via-gray-700/50" />
                </div>

                {/* New Password */}
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Min. 6 characters"
                      className={inputClass}
                    />
                  </div>
                  {/* Strength indicator */}
                  {newPassword && (
                    <div className="mt-2.5 flex items-center gap-3">
                      <div className="flex flex-1 gap-1">
                        {[1, 2, 3, 4, 5].map((level) => (
                          <div
                            key={level}
                            className="h-[3px] flex-1 rounded-full transition-all duration-500"
                            style={{
                              backgroundColor:
                                level <= passwordStrength
                                  ? strengthColor
                                  : isDarkMode
                                    ? 'rgba(255,255,255,0.06)'
                                    : 'rgba(0,0,0,0.08)',
                            }}
                          />
                        ))}
                      </div>
                      <span
                        className="text-[11px] font-semibold transition-colors"
                        style={{ color: strengthColor }}
                      >
                        {strengthLabel}
                      </span>
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter new password"
                      className={inputClass}
                    />
                    {/* Match indicator */}
                    {confirmPassword && newPassword && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {confirmPassword === newPassword ? (
                          <div className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-500/20">
                            <FaCheck size={9} className="text-emerald-600 dark:text-emerald-400" />
                          </div>
                        ) : (
                          <div className="flex h-5 w-5 items-center justify-center rounded-full bg-red-100 dark:bg-red-500/20">
                            <svg className="h-2.5 w-2.5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {confirmPassword && newPassword && confirmPassword !== newPassword && (
                    <p className="mt-1.5 text-xs font-medium text-red-500">Passwords don't match</p>
                  )}
                </div>
              </div>

              {/* Status Messages */}
              {error && (
                <div
                  className="mt-5 flex items-center gap-3 rounded-xl bg-red-50/80 px-4 py-3.5 dark:bg-red-500/[0.08]"
                  style={{ animation: 'settingsShake 0.4s ease-in-out' }}
                >
                  <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-500/20">
                    <svg className="h-3 w-3 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-[13px] font-medium text-red-600 dark:text-red-400">
                    {error}
                  </p>
                </div>
              )}

              {success && (
                <div className="mt-5 flex items-center gap-3 rounded-xl bg-emerald-50/80 px-4 py-3.5 dark:bg-emerald-500/[0.08]">
                  <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-emerald-500">
                    <FaCheck size={8} className="text-white" />
                  </div>
                  <p className="text-[13px] font-medium text-emerald-600 dark:text-emerald-400">
                    {success}
                  </p>
                </div>
              )}

              {/* Submit Button */}
              <div className="mt-7">
                <button
                  type="submit"
                  disabled={submitting}
                  className="group relative w-full overflow-hidden rounded-xl px-6 py-4 text-sm font-bold uppercase tracking-wider text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 disabled:pointer-events-none disabled:opacity-40"
                  style={{
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7)',
                    boxShadow: '0 4px 24px rgba(99, 102, 241, 0.3)',
                  }}
                >
                  <span
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                    style={{ transform: 'skewX(-20deg)' }}
                  />
                  <span className="relative flex items-center justify-center gap-2">
                    {submitting ? (
                      <>
                        <div
                          className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white"
                          style={{ animation: 'spin 0.6s linear infinite' }}
                        />
                        Updating...
                      </>
                    ) : (
                      'Update Password'
                    )}
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <Footer />

      {/* Keyframes */}
      <style>{`
        @keyframes settingsPulse {
          0% { transform: translate(0, 0) scale(1); }
          100% { transform: translate(30px, -20px) scale(1.1); }
        }
        @keyframes settingsShake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-6px); }
          40% { transform: translateX(6px); }
          60% { transform: translateX(-3px); }
          80% { transform: translateX(3px); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Settings;
